(() => {
  const FINDER_SELECTOR = '.sw-device-finder';
  const DEFAULT_ANCHOR = '#find-your-device';

  if (
    window.SlickwrapsDeviceFinder &&
    typeof window.SlickwrapsDeviceFinder.initAll === 'function'
  ) {
    window.SlickwrapsDeviceFinder.initAll();
    return;
  }

  const initializeFinder = (section) => {
    if (!section || section.dataset.initialized === 'true') return;

    section.dataset.initialized = 'true';

    const input = section.querySelector('.sw-device-finder__input');
    const searchArea = section.querySelector('.sw-device-finder__search');
    const clearButton = section.querySelector('.sw-device-finder__clear');
    const submitButton = section.querySelector('.sw-device-finder__submit');
    const resultsBox = section.querySelector('.sw-device-finder__results');

    const defaultContent = section.querySelector(
      '.sw-device-finder__default-content'
    );

    const categoryButtons = Array.from(
      section.querySelectorAll('.sw-device-finder__category')
    );

    const deviceSearch = window.SlickwrapsDeviceSearch;

    const popularList = section.querySelector(
      '.sw-device-finder__popular-list'
    );

    const popularPagination = section.querySelector(
      '[data-popular-pagination]'
    );

    let popularPaginationCount = 0;

    if (!input || !searchArea || !resultsBox || !deviceSearch) return;

    section._swDeviceFinderController?.abort();

    const controller = new AbortController();
    const signal = controller.signal;

    section._swDeviceFinderController = controller;

    const deviceBatchSize = Math.max(
      1,
      Number(section.dataset.deviceBatchSize) || 6
    );

    const mobileQuery = window.matchMedia('(max-width: 767px)');

    let interactionRequestId = 0;

    const escapeHTML = (value) =>
      String(value ?? '').replace(
        /[&<>"']/g,
        (character) =>
          ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;'
          })[character]
      );

    const normalize = (value) => deviceSearch.normalize(value);

    const getReleaseYear = (device) => {
      if (device.releaseYear === null || device.releaseYear === '' ||
          typeof device.releaseYear === 'undefined') return null;
      const year = Number(device.releaseYear);
      return Number.isInteger(year) && year >= 1900 && year <= 2200 ? year : null;
    };

    const compareText = (valueA, valueB) =>
      String(valueA || '').localeCompare(String(valueB || ''), undefined, {
        numeric: true, sensitivity: 'base'
      });

    const beginQuietPreload = () => {
      deviceSearch.load().catch(() => {});
    };

    const updatePopularPagination = () => {
      if (!popularList || !popularPagination) {
        return;
      }

      if (!mobileQuery.matches) {
        popularPagination.hidden = true;
        popularPagination.replaceChildren();
        popularPaginationCount = 0;
        return;
      }

      const firstCard = popularList.querySelector(
        '.sw-device-finder__popular-chip'
      );

      const listStyles = window.getComputedStyle(popularList);
      const gap =
        parseFloat(
          listStyles.columnGap ||
            listStyles.gap ||
            '0'
        ) || 0;

      const step = Math.max(
        1,
        (firstCard?.getBoundingClientRect().width ||
          popularList.clientWidth) + gap
      );

      const maximumScroll = Math.max(
        0,
        popularList.scrollWidth - popularList.clientWidth
      );

      if (maximumScroll <= 1) {
        popularPagination.hidden = true;
        popularPagination.replaceChildren();
        popularPaginationCount = 0;
        return;
      }

      const count = Math.max(
        1,
        Math.ceil(maximumScroll / step) + 1
      );

      if (count !== popularPaginationCount) {
        popularPagination.replaceChildren();
        popularPaginationCount = count;

        for (let index = 0; index < count; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className =
            'sw-device-finder__popular-pagination-dot';

          dot.setAttribute(
            'aria-label',
            `Show popular device slide ${index + 1} of ${count}`
          );

          dot.addEventListener(
            'click',
            () => {
              const currentMaximum = Math.max(
                0,
                popularList.scrollWidth -
                  popularList.clientWidth
              );

              popularList.scrollTo({
                left: Math.min(
                  index * step,
                  currentMaximum
                ),
                behavior: 'smooth'
              });
            },
            { signal }
          );

          popularPagination.appendChild(dot);
        }
      }

      popularPagination.hidden = false;

      const position = Math.min(
        maximumScroll,
        Math.max(0, popularList.scrollLeft)
      );

      const activeIndex = Math.min(
        popularPaginationCount - 1,
        Math.max(0, Math.round(position / step))
      );

      popularPagination
        .querySelectorAll(
          '.sw-device-finder__popular-pagination-dot'
        )
        .forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute(
            'aria-current',
            isActive ? 'true' : 'false'
          );
        });
    };

    const showDefaultContent = () => {
      interactionRequestId += 1;
      input.value = '';

      if (defaultContent) defaultContent.hidden = false;

      resultsBox.hidden = true;
      resultsBox.innerHTML = '';
      resultsBox.setAttribute('aria-busy', 'false');

      if (clearButton) clearButton.hidden = true;

      window.requestAnimationFrame(updatePopularPagination);
    };

    const showLoadingState = (message) => {
      if (defaultContent) defaultContent.hidden = true;

      resultsBox.hidden = false;
      resultsBox.setAttribute('aria-busy', 'true');
      resultsBox.innerHTML = `
        <div class="sw-device-finder__loading">
          <span
            class="sw-device-finder__loading-spinner"
            aria-hidden="true"
          ></span>
          <span>${escapeHTML(message)}</span>
        </div>
      `;
    };

    const showLoadError = (retry) => {
      if (defaultContent) defaultContent.hidden = true;
      resultsBox.hidden = false;
      resultsBox.setAttribute('aria-busy', 'false');
      resultsBox.innerHTML = `
        <div class="sw-device-finder__empty" role="status">
          <strong>Search could not load all devices.</strong>
          <span>Please retry or search again.</span>
          <button type="button" class="sw-device-finder__show-more" data-sw-finder-retry>Retry</button>
        </div>
      `;
      resultsBox.querySelector('[data-sw-finder-retry]')
        ?.addEventListener('click', retry, { signal });
    };

    const createTopBar = (heading, backLabel, backClass) => `
      <div class="sw-device-finder__results-topbar">
        <div class="sw-device-finder__results-title">
          ${escapeHTML(heading)}
        </div>

        <button
          type="button"
          class="sw-device-finder__inline-back ${backClass}"
        >
          <span aria-hidden="true">←</span>
          <span>${escapeHTML(backLabel)}</span>
        </button>
      </div>
    `;

    const deviceRowMarkup = (device) => {
      const title = escapeHTML(device.title);
      const brand = escapeHTML(device.brand);
      const url = escapeHTML(device.url);
      const imageURL = escapeHTML(device.image);
      const releaseYear = getReleaseYear(device);

      return `
        <a
          class="sw-device-finder__result"
          href="${url}"
          aria-label="Shop ${title} skins"
        >
          ${
            imageURL
              ? `
                <img
                  class="sw-device-finder__result-image"
                  src="${imageURL}"
                  alt="${title} skins and wraps"
                  loading="lazy"
                  width="54"
                  height="54"
                >
              `
              : ''
          }

          <span class="sw-device-finder__result-content">
            <span class="sw-device-finder__result-copy">
              <span class="sw-device-finder__result-title">
                ${title}
              </span>

              ${
                brand
                  ? `
                    <span class="sw-device-finder__result-meta">
                      ${brand}
                    </span>
                  `
                  : ''
              }

              ${
                releaseYear !== null
                  ? `
                    <span class="sw-device-finder__result-year">
                      ${releaseYear}
                    </span>
                  `
                  : ''
              }
            </span>

            <span class="sw-device-finder__result-action">
              View Skins
            </span>
          </span>
        </a>
      `;
    };

    const getBrandGroups = (category) => {
      const groups = new Map();

      deviceSearch.search('', { category })
        .forEach((device) => {
          const brandName =
            String(device.brand || '').trim() || 'Other';

          const brandKey = brandName.toLowerCase();

          if (!groups.has(brandKey)) {
            groups.set(brandKey, {
              name: brandName,
              devices: []
            });
          }

          groups.get(brandKey).devices.push(device);
        });

      return Array.from(groups.values())
        .sort((groupA, groupB) => {
          if (groupA.name === 'Other') return 1;
          if (groupB.name === 'Other') return -1;

          return compareText(groupA.name, groupB.name);
        });
    };

    const renderBrandScreen = (category) => {
      const brandGroups = getBrandGroups(category);
      if (defaultContent) defaultContent.hidden = true;
      resultsBox.setAttribute('aria-busy', 'false');

      const deviceCount = brandGroups.reduce(
        (total, group) => total + group.devices.length,
        0
      );

      if (!brandGroups.length) {
        resultsBox.innerHTML = `
          ${createTopBar(
            category,
            'Back to categories',
            'sw-device-finder__back-categories'
          )}

          <div class="sw-device-finder__empty">
            <strong>No devices added yet</strong>
            <span>There are currently no devices in this category.</span>
          </div>
        `;
      } else {
        const brandCards = brandGroups
          .map(
            (group, index) => `
              <button
                type="button"
                class="sw-device-finder__brand-card"
                data-brand-index="${index}"
              >
                <span class="sw-device-finder__brand-card-copy">
                  <span class="sw-device-finder__brand-name">
                    ${escapeHTML(group.name)}
                  </span>

                  <span class="sw-device-finder__brand-count">
                    ${group.devices.length}
                    ${group.devices.length === 1 ? 'device' : 'devices'}
                  </span>
                </span>

                <span
                  class="sw-device-finder__brand-arrow"
                  aria-hidden="true"
                >
                  →
                </span>
              </button>
            `
          )
          .join('');

        resultsBox.innerHTML = `
          ${createTopBar(
            `${category} · ${deviceCount} devices`,
            'Back to categories',
            'sw-device-finder__back-categories'
          )}

          <div class="sw-device-finder__brand-grid">
            ${brandCards}
          </div>
        `;
      }

      resultsBox.hidden = false;

      resultsBox
        .querySelector('.sw-device-finder__back-categories')
        ?.addEventListener('click', showDefaultContent, {
          signal
        });

      resultsBox
        .querySelectorAll('.sw-device-finder__brand-card')
        .forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              const group =
                brandGroups[Number(button.dataset.brandIndex)];

              if (group) renderDeviceScreen(category, group);
            },
            {
              signal
            }
          );
        });
    };

    const renderDeviceScreen = (category, brandGroup) => {
      // Brand groups retain the shared search module's ordering.
      const brandDevices = brandGroup.devices;
      resultsBox.setAttribute('aria-busy', 'false');

      resultsBox.innerHTML = `
        ${createTopBar(
          `${brandGroup.name} · ${brandDevices.length} ${
            brandDevices.length === 1 ? 'device' : 'devices'
          }`,
          'Back to brands',
          'sw-device-finder__back-brands'
        )}

        <div class="sw-device-finder__device-list"></div>

        <div
          class="sw-device-finder__show-more-wrap"
          hidden
        >
          <button
            type="button"
            class="sw-device-finder__show-more"
          >
            Show More Devices
          </button>
        </div>
      `;

      resultsBox.hidden = false;

      const deviceList = resultsBox.querySelector(
        '.sw-device-finder__device-list'
      );

      const showMoreWrap = resultsBox.querySelector(
        '.sw-device-finder__show-more-wrap'
      );

      const showMoreButton = resultsBox.querySelector(
        '.sw-device-finder__show-more'
      );

      let visibleCount = 0;

      const appendNextDevices = () => {
        const nextDevices = brandDevices.slice(
          visibleCount,
          visibleCount + deviceBatchSize
        );

        deviceList.insertAdjacentHTML(
          'beforeend',
          nextDevices.map(deviceRowMarkup).join('')
        );

        visibleCount += nextDevices.length;

        const remaining = brandDevices.length - visibleCount;

        if (remaining > 0) {
          showMoreWrap.hidden = false;
          showMoreButton.textContent =
            `Show More Devices (${remaining} remaining)`;
        } else {
          showMoreWrap.hidden = true;
        }
      };

      resultsBox
        .querySelector('.sw-device-finder__back-brands')
        ?.addEventListener(
          'click',
          () => renderBrandScreen(category),
          {
            signal
          }
        );

      showMoreButton?.addEventListener('click', appendNextDevices, {
        signal
      });

      appendNextDevices();
    };

    const renderSearchResults = (matches, searchLabel) => {
      if (defaultContent) defaultContent.hidden = true;

      resultsBox.hidden = false;
      resultsBox.setAttribute('aria-busy', 'false');

      if (!matches.length) {
        resultsBox.innerHTML = `
          <div class="sw-device-finder__empty">
            <strong>Can’t find your device?</strong>

            <span>
              Request it and help us decide what to support next.
            </span>

            <a
              href="/collections/new-devices#request-a-device"
              class="sw-device-finder__request"
            >
              Request a Device
            </a>
          </div>
        `;

        return;
      }

      resultsBox.innerHTML = `
        <div class="sw-device-finder__results-title">
          ${matches.length}
          ${matches.length === 1 ? 'result' : 'results'}
          for “${escapeHTML(searchLabel)}”
        </div>

        <div class="sw-device-finder__device-list"></div>

        <div
          class="sw-device-finder__show-more-wrap"
          hidden
        >
          <button
            type="button"
            class="sw-device-finder__show-more"
          >
            Show More Results
          </button>
        </div>
      `;

      const resultList = resultsBox.querySelector(
        '.sw-device-finder__device-list'
      );

      const showMoreWrap = resultsBox.querySelector(
        '.sw-device-finder__show-more-wrap'
      );

      const showMoreButton = resultsBox.querySelector(
        '.sw-device-finder__show-more'
      );

      let visibleCount = 0;

      const appendNextResults = () => {
        const nextResults = matches.slice(
          visibleCount,
          visibleCount + deviceBatchSize
        );

        resultList.insertAdjacentHTML(
          'beforeend',
          nextResults.map(deviceRowMarkup).join('')
        );

        visibleCount += nextResults.length;

        const remaining = matches.length - visibleCount;

        if (remaining > 0) {
          showMoreWrap.hidden = false;
          showMoreButton.textContent =
            `Show More Results (${remaining} remaining)`;
        } else {
          showMoreWrap.hidden = true;
        }
      };

      showMoreButton?.addEventListener('click', appendNextResults, {
        signal
      });

      appendNextResults();
    };

    const runSearch = async () => {
      const rawQuery = input.value.trim();
      const query = normalize(rawQuery);
      const currentRequestId = ++interactionRequestId;

      if (clearButton) clearButton.hidden = !input.value.length;

      if (!query.length) {
        showDefaultContent();
        return;
      }

      if (query.length < 2) {
        if (defaultContent) defaultContent.hidden = true;

        resultsBox.hidden = false;
        resultsBox.setAttribute('aria-busy', 'false');
        resultsBox.innerHTML = `
          <div class="sw-device-finder__empty">
            Keep typing to search your device.
          </div>
        `;

        return;
      }

      if (!deviceSearch.isComplete()) {
        showLoadingState('Searching all devices…');
      }

      try {
        await deviceSearch.load();
      } catch (error) {
        if (!signal.aborted && currentRequestId === interactionRequestId &&
            normalize(input.value) === query) showLoadError(runSearch);
        return;
      }

      if (
        signal.aborted ||
        currentRequestId !== interactionRequestId ||
        normalize(input.value) !== query
      ) {
        return;
      }

      renderSearchResults(deviceSearch.search(query), rawQuery);
    };

    const openCategory = async (category) => {
      const currentRequestId = ++interactionRequestId;
      input.value = '';
      if (clearButton) clearButton.hidden = true;
      if (!deviceSearch.isComplete()) showLoadingState(`Loading ${category}…`);

      try {
        await deviceSearch.load();
      } catch (error) {
        if (!signal.aborted && currentRequestId === interactionRequestId) {
          showLoadError(() => openCategory(category));
        }
        return;
      }

      if (signal.aborted || currentRequestId !== interactionRequestId) return;
      renderBrandScreen(category);
    };

    input.addEventListener('focus', beginQuietPreload, {
      signal
    });

    input.addEventListener('input', runSearch, {
      signal
    });

    input.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          showDefaultContent();
          input.blur();
        }

        if (event.key === 'Enter') {
          event.preventDefault();
          runSearch();
        }
      },
      {
        signal
      }
    );

    clearButton?.addEventListener(
      'click',
      () => {
        showDefaultContent();
        input.focus();
      },
      {
        signal
      }
    );

    submitButton?.addEventListener(
      'click',
      () => {
        input.focus();
        runSearch();
      },
      {
        signal
      }
    );

    categoryButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => openCategory(button.dataset.category),
        {
          signal
        }
      );
    });

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (
          searchArea.contains(event.target) ||
          resultsBox.contains(event.target) ||
          event.target.closest('.sw-device-finder__category') ||
          event.target.closest('.sw-device-finder__view-all-link')
        ) {
          return;
        }

        if (!resultsBox.hidden || input.value.length) {
          showDefaultContent();
        }
      },
      {
        signal
      }
    );

    popularList?.addEventListener('scroll', updatePopularPagination, {
      passive: true,
      signal
    });

    window.addEventListener('resize', updatePopularPagination, {
      signal
    });

    const handleMobileChange = updatePopularPagination;

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleMobileChange);

      signal.addEventListener(
        'abort',
        () => {
          mobileQuery.removeEventListener('change', handleMobileChange);
        },
        {
          once: true
        }
      );
    }

    showDefaultContent();
    window.requestAnimationFrame(updatePopularPagination);

  };

  const initializeAllFinders = () => {
    document
      .querySelectorAll(FINDER_SELECTOR)
      .forEach(initializeFinder);
  };

  const initializeAnchorScrolling = () => {
    if (window.__swDeviceFinderAnchorReady) return;

    window.__swDeviceFinderAnchorReady = true;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a');

      if (!link) return;

      let linkURL;

      try {
        linkURL = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }

      if (
        linkURL.hash !== DEFAULT_ANCHOR ||
        linkURL.origin !== window.location.origin ||
        linkURL.pathname !== window.location.pathname
      ) {
        return;
      }

      const finder = document.querySelector(DEFAULT_ANCHOR);

      if (!finder) return;

      event.preventDefault();

      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      finder.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });

      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}${DEFAULT_ANCHOR}`
      );
    });
  };

  window.SlickwrapsDeviceFinder = {
    init: initializeFinder,
    initAll: initializeAllFinders
  };

  initializeAnchorScrolling();

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAllFinders,
      {
        once: true
      }
    );
  } else {
    initializeAllFinders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const finder = event.target.matches?.(FINDER_SELECTOR)
      ? event.target
      : event.target.querySelector(FINDER_SELECTOR);

    if (finder) {
      finder.dataset.initialized = 'false';
      initializeFinder(finder);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const finder = event.target.matches?.(FINDER_SELECTOR)
      ? event.target
      : event.target.querySelector(FINDER_SELECTOR);

    finder?._swDeviceFinderController?.abort();
  });
})();
